// Agora Video Call Component
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  CallEnd as CallEndIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { VideoCallSession } from '../types';

// Use app_id from backend response

interface AgoraVideoCallProps {
  callSession: VideoCallSession;
  onEndCall: () => void;
  onCallStateChange?: (state: CallState) => void;
}

interface CallState {
  localVideoEnabled: boolean;
  localAudioEnabled: boolean;
  remoteVideoEnabled: boolean;
  remoteAudioEnabled: boolean;
  isConnected: boolean;
  connectionQuality: 'excellent' | 'good' | 'poor' | 'unknown';
}

const AgoraVideoCall: React.FC<AgoraVideoCallProps> = ({ callSession, onEndCall, onCallStateChange }) => {
  // Agora SDK state
  const [isInitialized, setIsInitialized] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Call state
  const [callState, setCallState] = useState<CallState>({
    localVideoEnabled: true,
    localAudioEnabled: true,
    remoteVideoEnabled: true,
    remoteAudioEnabled: true,
    isConnected: false,
    connectionQuality: 'unknown',
  });

  // Refs for video elements
  const localVideoRef = useRef<HTMLDivElement>(null);
  const remoteVideoRef = useRef<HTMLDivElement>(null);
  const agoraClientRef = useRef<any>(null);
  const localTracksRef = useRef<any>(null);
  const hasJoinedRef = useRef<boolean>(false);

  // Initialize Agora client
  useEffect(() => {
    const initializeAgora = async () => {
      try {
        // Check if we're in a secure context (HTTPS or localhost)
        if (!window.isSecureContext && window.location.hostname !== 'localhost') {
          setError('Video calls require HTTPS or localhost. Please use a secure connection.');
          return;
        }

        // Check browser compatibility
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setError('Your browser does not support video calls. Please use Chrome, Firefox, or Edge.');
          return;
        }

        // Dynamic import to avoid SSR issues
        const AgoraRTC = await import('agora-rtc-sdk-ng');

        // Create Agora client - Use live mode for dashboard users
        const client = AgoraRTC.default.createClient({
          mode: 'live', // Live mode with publisher role
          codec: 'h264',
        });

        agoraClientRef.current = client;

        // Set up event listeners
        client.on('user-published', async (user: any, mediaType: any) => {
          console.log('🔊 Remote user published:', user, mediaType);
          console.log('🔊 User UID:', user.uid);
          console.log('🔊 Media type:', mediaType);

          try {
            // Subscribe to remote user
            await client.subscribe(user, mediaType);
            console.log('✅ Successfully subscribed to remote user');

            if (mediaType === 'video') {
              const remoteVideoTrack = user.videoTrack;
              console.log('📹 Remote video track:', remoteVideoTrack);
              if (remoteVideoTrack && remoteVideoRef.current) {
                console.log('📹 Playing remote video in element:', remoteVideoRef.current);
                await remoteVideoTrack.play(remoteVideoRef.current);
                console.log('✅ Remote video playing successfully');
              } else {
                console.warn('❌ Cannot play remote video:', {
                  remoteVideoTrack: !!remoteVideoTrack,
                  remoteVideoRef: !!remoteVideoRef.current,
                });
              }
            }

            if (mediaType === 'audio') {
              const remoteAudioTrack = user.audioTrack;
              console.log('🔊 Remote audio track:', remoteAudioTrack);
              if (remoteAudioTrack) {
                await remoteAudioTrack.play();
                console.log('✅ Remote audio playing successfully');
              }
            }

            setCallState(prev => ({
              ...prev,
              isConnected: true,
              remoteVideoEnabled: mediaType === 'video' ? true : prev.remoteVideoEnabled,
              remoteAudioEnabled: mediaType === 'audio' ? true : prev.remoteAudioEnabled,
            }));
          } catch (error) {
            console.error('❌ Error subscribing to remote user:', error);
          }
        });

        client.on('user-unpublished', (user: any, mediaType: any) => {
          console.log('🔇 Remote user unpublished:', user, mediaType);

          if (mediaType === 'video') {
            setCallState(prev => ({ ...prev, remoteVideoEnabled: false }));
          }

          if (mediaType === 'audio') {
            setCallState(prev => ({ ...prev, remoteAudioEnabled: false }));
          }
        });

        client.on('user-joined', (user: any) => {
          console.log('👋 Remote user joined:', user);
          console.log('👋 User UID:', user.uid);
        });

        client.on('user-left', (user: any) => {
          console.log('👋 Remote user left:', user);
          console.log('👋 User UID:', user.uid);
          setCallState(prev => ({ ...prev, isConnected: false }));
        });

        client.on('connection-state-change', (curState: any, revState: any) => {
          console.log('🔗 Connection state changed:', curState, revState);

          if (curState === 'CONNECTED') {
            setCallState(prev => ({ ...prev, isConnected: true }));
          } else if (curState === 'DISCONNECTED') {
            setCallState(prev => ({ ...prev, isConnected: false }));
          }
        });

        setIsInitialized(true);
        console.log('✅ Agora client initialized');
      } catch (err) {
        console.error('❌ Error initializing Agora:', err);
        setError('Failed to initialize video call system');
      }
    };

    initializeAgora();

    // Cleanup on unmount
    return () => {
      const cleanup = async () => {
        if (localTracksRef.current) {
          localTracksRef.current.forEach((track: any) => {
            try {
              track.stop();
              track.close();
            } catch (err) {
              console.warn('Error stopping track:', err);
            }
          });
          localTracksRef.current = null;
        }

        if (agoraClientRef.current) {
          try {
            await agoraClientRef.current.leave();
          } catch (err) {
            console.warn('Error leaving channel:', err);
          }
        }
      };
      cleanup();
    };
  }, []);

  // Join channel when call session is available
  useEffect(() => {
    console.log('🔍 useEffect triggered:', {
      isInitialized,
      callSession: !!callSession,
      hasJoined: hasJoinedRef.current,
    });

    if (!isInitialized || !callSession || hasJoinedRef.current) {
      console.log('❌ Not joining channel:', {
        isInitialized,
        hasCallSession: !!callSession,
        hasJoined: hasJoinedRef.current,
      });
      return;
    }

    console.log('✅ All conditions met, proceeding to join channel');
    hasJoinedRef.current = true;

    const joinChannel = async () => {
      try {
        setIsConnecting(true);
        setError(null);

        const AgoraRTC = await import('agora-rtc-sdk-ng');
        const client = agoraClientRef.current;

        // Request media permissions first
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true,
          });
          // Stop the test stream
          stream.getTracks().forEach(track => track.stop());
        } catch (mediaError) {
          console.error('❌ Media access denied:', mediaError);
          setError('Camera and microphone access is required for video calls. Please allow access and try again.');
          return;
        }

        // Create local tracks
        const [audioTrack, videoTrack] = await AgoraRTC.default.createMicrophoneAndCameraTracks(
          {},
          {
            encoderConfig: {
              width: 640,
              height: 480,
              frameRate: 15,
            },
          },
        );

        localTracksRef.current = [audioTrack, videoTrack];

        // Play local video
        if (videoTrack && localVideoRef.current) {
          videoTrack.play(localVideoRef.current);
        }

        // Join channel using generated token

        await client.join(
          callSession.app_id!,
          callSession.channel_name,
          callSession.agora_token, // Use the actual token from external server
          null, // Let Agora auto-assign UID to avoid conflicts
        );

        // Set client role to host (publisher) in live mode
        await client.setClientRole('host');

        // Publish local tracks
        await client.publish([audioTrack, videoTrack]);

        console.log('🎥 Successfully joined channel:', callSession.channel_name);
        setCallState(prev => ({ ...prev, isConnected: true }));
      } catch (err: any) {
        console.error('❌ Error joining channel:', err);

        // Provide more specific error messages
        if (err.code === 'NOT_SUPPORTED') {
          setError('Your browser does not support video calls. Please use Chrome, Firefox, or Edge.');
        } else if (err.code === 'WEB_SECURITY_RESTRICT') {
          setError('Video calls require HTTPS or localhost. Please use a secure connection.');
        } else if (err.name === 'NotAllowedError') {
          setError('Camera and microphone access was denied. Please allow access and try again.');
        } else if (err.name === 'NotFoundError') {
          setError('No camera or microphone found. Please check your devices.');
        } else {
          setError(`Failed to join video call: ${err.message || 'Unknown error'}`);
        }
      } finally {
        setIsConnecting(false);
      }
    };

    joinChannel();
  }, [isInitialized, callSession]);

  // Update call state callback (memoize to prevent unnecessary re-renders)
  useEffect(() => {
    if (onCallStateChange) {
      onCallStateChange(callState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState]);

  // Toggle local audio
  const toggleLocalAudio = useCallback(async () => {
    if (!localTracksRef.current) return;

    const audioTrack = localTracksRef.current[0];
    if (audioTrack) {
      await audioTrack.setEnabled(!callState.localAudioEnabled);
      setCallState(prev => ({ ...prev, localAudioEnabled: !prev.localAudioEnabled }));
    }
  }, [callState.localAudioEnabled]);

  // Toggle local video
  const toggleLocalVideo = useCallback(async () => {
    if (!localTracksRef.current) return;

    const videoTrack = localTracksRef.current[1];
    if (videoTrack) {
      await videoTrack.setEnabled(!callState.localVideoEnabled);
      setCallState(prev => ({ ...prev, localVideoEnabled: !prev.localVideoEnabled }));
    }
  }, [callState.localVideoEnabled]);

  // End call
  const handleEndCall = async () => {
    try {
      // Unpublish tracks first
      if (localTracksRef.current && agoraClientRef.current) {
        try {
          await agoraClientRef.current.unpublish(localTracksRef.current);
        } catch (err) {
          console.warn('Error unpublishing tracks:', err);
        }
      }

      // Leave channel
      if (agoraClientRef.current) {
        try {
          await agoraClientRef.current.leave();
        } catch (err) {
          console.warn('Error leaving channel:', err);
        }
      }

      // Stop and close tracks completely
      if (localTracksRef.current) {
        localTracksRef.current.forEach((track: any) => {
          try {
            track.stop();
            track.close();
          } catch (err) {
            console.warn('Error stopping track:', err);
          }
        });
        localTracksRef.current = null;
      }

      // Clear video element
      if (localVideoRef.current && 'srcObject' in localVideoRef.current) {
        (localVideoRef.current as unknown as HTMLVideoElement).srcObject = null;
      }

      // Release media devices
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          // Force release by requesting empty stream then stopping it
          const stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: false });
          stream.getTracks().forEach(track => track.stop());
        }
      } catch (err) {
        console.warn('Error releasing media devices:', err);
      }

      onEndCall();
    } catch (err) {
      console.error('❌ Error ending call:', err);
      onEndCall(); // Still call onEndCall to clean up UI
    }
  };

  // Get connection quality color
  const getConnectionQualityColor = () => {
    switch (callState.connectionQuality) {
      case 'excellent':
        return 'success';
      case 'good':
        return 'info';
      case 'poor':
        return 'warning';
      default:
        return 'default';
    }
  };

  const renderCallContent = () => {
    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>

          {/* Browser compatibility information */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Browser Requirements:</strong>
            </Typography>
            <Typography variant="body2" component="div">
              • <strong>Chrome/Edge:</strong> Full support ✅<br />• <strong>Firefox:</strong> Full support ✅<br />•{' '}
              <strong>Safari:</strong> Limited support ⚠️
              <br />• <strong>HTTPS Required:</strong> Video calls need secure connection 🔒
            </Typography>
          </Alert>

          <Button onClick={onEndCall} variant="contained" color="error">
            Close Call
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Call Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Video Call - {callSession.robot_id}</Typography>
              <Typography variant="body2" color="text.secondary">
                Channel: {callSession.channel_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label={callState.isConnected ? 'Connected' : 'Connecting...'}
                color={callState.isConnected ? 'success' : 'warning'}
                size="small"
              />
              <Chip label={callState.connectionQuality} color={getConnectionQualityColor() as any} size="small" />
              <Tooltip title="Close call">
                <IconButton onClick={onEndCall} color="error" size="small" sx={{ ml: 1 }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Video Container - Optimized for portrait mode robot video */}
        <Box sx={{ flex: 1, p: 2, display: 'flex', gap: 3, alignItems: 'flex-start', justifyContent: 'center' }}>
          {/* Remote Video (Robot) - Portrait mode optimized */}
          <Paper
            sx={{
              width: '50%',
              maxWidth: '60%',
              minHeight: 420,
              maxHeight: '70vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'grey.900',
              position: 'relative',
              aspectRatio: '9/16', // Portrait aspect ratio (typical phone/robot camera)
            }}
          >
            {isConnecting ? (
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Connecting to robot...
                </Typography>
              </Box>
            ) : callState.remoteVideoEnabled ? (
              <Box
                ref={remoteVideoRef}
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& video': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain', // Maintain aspect ratio
                  },
                }}
              />
            ) : (
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <VideocamOffIcon sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="body2">Robot camera is off</Typography>
              </Box>
            )}
          </Paper>

          {/* Local Video */}
          <Paper
            sx={{
              width: 320,
              height: 240,
              backgroundColor: 'grey.800',
              position: 'relative',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {callState.localVideoEnabled ? (
              <Box
                ref={localVideoRef}
                sx={{
                  width: '100%',
                  height: '100%',
                  '& video': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  },
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <VideocamOffIcon />
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 4,
                left: 4,
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.5)',
                px: 1,
                borderRadius: 1,
              }}
            >
              You
            </Typography>
          </Paper>
        </Box>

        {/* Call Controls */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Tooltip title={callState.localAudioEnabled ? 'Mute' : 'Unmute'}>
              <IconButton
                onClick={toggleLocalAudio}
                color={callState.localAudioEnabled ? 'primary' : 'error'}
                disabled={isConnecting}
              >
                {callState.localAudioEnabled ? <MicIcon /> : <MicOffIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title={callState.localVideoEnabled ? 'Turn off camera' : 'Turn on camera'}>
              <IconButton
                onClick={toggleLocalVideo}
                color={callState.localVideoEnabled ? 'primary' : 'error'}
                disabled={isConnecting}
              >
                {callState.localVideoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title="End call">
              <IconButton
                onClick={handleEndCall}
                color="error"
                disabled={isConnecting}
                sx={{ backgroundColor: 'error.main', color: 'white' }}
              >
                <CallEndIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open
      onClose={() => null}
      maxWidth="lg"
      fullWidth
      fullScreen
      BackdropProps={{
        sx: { backgroundColor: 'rgba(128,128,128,0.6)' },
      }}
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          height: '100%',
          borderRadius: 0,
        },
      }}
    >
      <DialogContent sx={{ p: 0, height: '100%' }}>{renderCallContent()}</DialogContent>
    </Dialog>
  );
};

export default AgoraVideoCall;
